import { useCallback, useEffect, useMemo, useState } from 'react';
import { newId, type Repositories } from '@core/db';
import type { FileStore } from '@core/files';
import { telemetry } from '@core/telemetry';
import { unsortedTitle } from './imports';

/**
 * Быстрый скан: снятое кладётся в дело, выбранное **после** съёмки.
 *
 * Порядок именно такой — порт `QuickScanViewModel` Android-версии. В суде снимают
 * материалы, а не заводят карточки: спросить «в какое дело» до съёмки значит поставить
 * анкету между юристом и уходящим томом. Дело обязательно, название документа — нет:
 * разобрать снятое можно и вечером, безымянное помечается «Не разобрано».
 */

/** Дело, куда можно положить свежий скан. */
export interface QuickScanCase {
  id: string;
  title: string;
}

export interface QuickScanState {
  cases: QuickScanCase[];
  isSaving: boolean;
  errorMessage: string | null;
}

export function useQuickScan(db: Repositories, files: FileStore) {
  const [cases, setCases] = useState<QuickScanCase[]>([]);
  const [isSaving, setSaving] = useState(false);
  const [errorMessage, setError] = useState<string | null>(null);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const all = await db.cases.list({});
      if (!dropped) setCases(all.map((item) => ({ id: item.id, title: item.title })));
    })();

    return () => {
      dropped = true;
    };
  }, [db]);

  const state: QuickScanState = useMemo(
    () => ({ cases, isSaving, errorMessage }),
    [cases, isSaving, errorMessage],
  );

  /**
   * Кладёт страницу в дело, заводя дело, если его ещё нет.
   *
   * Возвращает дело, в которое положили, — каркасу это нужно, чтобы открыть его
   * сразу после сохранения: юрист снимал не в пустоту, и результат надо показать.
   */
  const save = useCallback(
    async (choice: {
      caseId: string | null;
      newCaseTitle: string;
      documentTitle: string;
      page: Blob;
    }): Promise<string | null> => {
      setSaving(true);
      setError(null);

      try {
        let caseId = choice.caseId;

        if (!caseId) {
          const created = await db.cases.create({
            id: '',
            title: choice.newCaseTitle.trim(),
            clientId: null,
            // Тип дела здесь не спрашиваем: он ставится в карточке, а снимающему
            // в коридоре суда лишний вопрос дороже точности.
            type: 'Other',
            status: 'Active',
            typeOther: null,
            description: null,
            notes: null,
            links: [],
            isPinned: false,
            sortOrder: 0,
          });

          if (!created.ok) {
            setError('Не удалось завести дело');
            return null;
          }
          telemetry.record('caseCreated');
          caseId = created.value;
        }

        const id = newId();
        const stored = await files.store(caseId, id, 'page.jpg', choice.page);
        if (!stored.ok) {
          setError('Не удалось сохранить страницу');
          return null;
        }

        const added = await db.documents.add({
          id,
          title: choice.documentTitle.trim() || unsortedTitle(new Date()),
          caseId,
          clientId: null,
          fileName: stored.value.fileName,
          mimeType: 'image/jpeg',
          sizeBytes: stored.value.sizeBytes,
          pageCount: null,
          isFavorite: false,
          type: 'Other',
          source: 'Photo',
        });

        if (!added.ok) {
          setError('Не удалось сохранить документ');
          return null;
        }

        // Быстрый скан — это сканирование: страница пришла из правки границ листа,
        // а не из галереи. Тот же счётчик, что и у камеры в библиотеке документов.
        telemetry.record('documentScanned');
        return caseId;
      } finally {
        setSaving(false);
      }
    },
    [db, files],
  );

  return { state, save, errorShown: useCallback(() => setError(null), []) };
}
